import React from 'react';
import { Link } from 'react-router-dom';
import { Leaf, Sparkles, Shield, Star } from 'lucide-react';
import { companyInfo, testimonials, galleryImages } from '../data/mockData';
import { SiteVideo } from '../components/SiteVideo';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4">
        <div className="absolute inset-0 bg-gradient-to-br from-green-50 via-white to-green-50 -z-10" />
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Texte et boutons à gauche */}
            <div>
              <h1 data-testid="home-page-title" className="text-4xl md:text-6xl font-bold mb-6 text-gray-900">
                Bienvenue sur le site de l'équipe{' '}
                <span className="text-gray-900">Perinature</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed mb-8">
                {companyInfo.description}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  data-testid="home-hero-contact-link"
                  className="px-8 py-4 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-all duration-200 hover:scale-105 shadow-lg"
                >
                  Demander un devis
                </Link>
                <Link
                  to="/conception"
                  data-testid="home-services-link"
                  className="px-8 py-4 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-200 hover:scale-105 shadow-lg border-2 border-gray-200"
                >
                  Nos services
                </Link>
              </div>
            </div>

            {/* Vidéo à droite */}
            <div className="flex items-center justify-center">
              <SiteVideo name="home" label="Présentation de Perinature" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
            Nos Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Link
              to="/conception"
              data-testid="home-conception-service-link"
              className="group p-8 rounded-2xl bg-gradient-to-br from-orange-50 to-white border-2 border-orange-100 hover:border-orange-300 transition-all duration-300 hover:scale-105 hover:shadow-xl"
            >
              <div className="w-16 h-16 bg-orange-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Sparkles className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-orange-600">Conception</h3>
              <p className="text-gray-600">
                Plans d'aménagement personnalisés pour créer l'espace vert de vos rêves.
              </p>
            </Link>

            <Link
              to="/creation"
              data-testid="home-creation-service-link"
              className="group p-8 rounded-2xl bg-gradient-to-br from-red-50 to-white border-2 border-red-100 hover:border-red-300 transition-all duration-300 hover:scale-105 hover:shadow-xl"
            >
              <div className="w-16 h-16 bg-red-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Leaf className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-red-600">Création</h3>
              <p className="text-gray-600">
                Réalisation complète de vos projets d'aménagement paysager.
              </p>
            </Link>

            <Link
              to="/entretien"
              data-testid="home-entretien-service-link"
              className="group p-8 rounded-2xl bg-gradient-to-br from-green-50 to-white border-2 border-green-100 hover:border-green-300 transition-all duration-300 hover:scale-105 hover:shadow-xl"
            >
              <div className="w-16 h-16 bg-green-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <Shield className="text-white" size={32} />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-green-700">Entretien</h3>
              <p className="text-gray-600">
                Maintenance régulière avec avantage fiscal : 50% de crédit d'impôt.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="py-20 px-4 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-gray-900">
            Nos Réalisations
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {galleryImages.filter(img => img.category === 'home' || img.category === 'entretien').slice(0, 4).map((image) => (
              <div
                key={image.id}
                data-testid={`home-gallery-item-${image.id}`}
                className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105"
              >
                <img
                  src={image.url}
                  data-testid={`home-gallery-image-${image.id}`}
                  alt={image.title}
                  className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                    <h3 className="font-bold text-lg">{image.title}</h3>
                    <p className="text-sm text-gray-200">{image.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-gray-900">
            Témoignages Clients
          </h2>
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="text-yellow-400 fill-yellow-400" size={24} />
              ))}
            </div>
            <p data-testid="home-google-rating" className="text-gray-600 mt-2">Note moyenne 5/5 sur Google</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                data-testid={`home-testimonial-${testimonial.id}`}
                className="p-6 rounded-xl bg-gradient-to-br from-gray-50 to-white border-2 border-gray-100 hover:border-green-200 transition-all duration-300 hover:shadow-lg"
              >
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="text-yellow-400 fill-yellow-400" size={16} />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                <p className="text-sm text-gray-500 text-right">{testimonial.date}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-green-600 to-green-700">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
            Prêt à transformer votre jardin ?
          </h2>
          <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
            Contactez-nous pour un devis gratuit et personnalisé. Notre équipe est à votre écoute.
          </p>
          <Link
            to="/contact"
            data-testid="home-bottom-contact-link"
            className="inline-block px-8 py-4 bg-white text-green-700 rounded-lg font-semibold hover:bg-green-50 transition-all duration-200 hover:scale-105 shadow-xl"
          >
            Demander un devis gratuit
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;